import * as React from 'react';
import { Inbox, Plus } from 'lucide-react';
import { Button } from './button';
import { TableRow, TableCell } from './table';
import { cn } from '@/lib/utils';

export interface EmptyStateProps {
  /**
   * Title text displayed prominently
   */
  title: string;
  /**
   * Optional description text displayed below the title
   */
  description?: string;
  /**
   * Optional icon to display (defaults to Inbox icon)
   */
  icon?: React.ReactNode;
  /**
   * Optional action button configuration
   */
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link';
  };
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Size variant for the empty state
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Reusable EmptyState component for displaying empty data states.
 * Provides consistent styling and optional action buttons for better UX.
 *
 * @example
 * ```tsx
 * <EmptyState
 *   title="No dogs found"
 *   description="Get started by creating your first dog record"
 *   action={{
 *     label: "Add Dog",
 *     onClick: () => setIsAddDialogOpen(true)
 *   }}
 * />
 * ```
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  size = 'md',
}: EmptyStateProps) {
  const sizeClasses = {
    sm: 'py-8',
    md: 'py-12',
    lg: 'py-16',
  };

  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
  };

  const defaultIcon = <Inbox className={cn('text-muted-foreground', iconSizeClasses[size])} />;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizeClasses[size],
        className,
      )}
    >
      <div className={cn('mb-4 text-muted-foreground', iconSizeClasses[size])}>
        {icon || defaultIcon}
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      {description && <p className="text-sm text-muted-foreground max-w-sm mb-6">{description}</p>}
      {action && (
        <Button
          onClick={action.onClick}
          variant={action.variant || 'default'}
          size={size === 'sm' ? 'sm' : 'default'}
        >
          <Plus className="h-4 w-4" />
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * EmptyState variant specifically for table empty states.
 * Designed to be used within TableBody components.
 * Returns a TableRow and TableCell that can be used directly in tables.
 */
export function TableEmptyState({
  title,
  description,
  action,
  colSpan,
}: {
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  colSpan: number;
}) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-32">
        <EmptyState
          title={title}
          description={description}
          action={action}
          size="sm"
          className="py-6"
        />
      </TableCell>
    </TableRow>
  );
}
